import React from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import HomeProductOverview from "./HomeProductOverview";
import {
  MessageOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  NodeIndexOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import StaticOpsChatPreview from "../../components/home/StaticOpsChatPreview";
import { APP_NAME } from "../../helpers/constants";

const AI_FIRST_RESPONDER_DEMO_MESSAGES = [
  {
    role: "user",
    sender: "Tenant",
    time: "11:47 PM",
    text: (
      <>
        &quot;URGENT, kitchen faucet dripping&quot; · Tenant thread opened
      </>
    ),
  },
  {
    role: "assistant",
    time: "11:47 PM",
    text: (
      <>
        <strong>Update:</strong> Handled in their thread and logged for you.
        They&apos;re covered. Details are in your dashboard Monday. No need to
        respond tonight.
      </>
    ),
  },
  {
    role: "user",
    sender: "Tenant",
    time: "Sat 8:01 AM",
    text: (
      <>
        &quot;Can I paint the walls?&quot; · Tenant thread opened
      </>
    ),
  },
  {
    role: "assistant",
    time: "Sat 8:01 AM",
    text: (
      <>
        <strong>Update:</strong> Answered in their thread: written approval
        required for paint. They&apos;re set. Review when you want.
      </>
    ),
  },
];

export default function HomeFullOsSuite() {
  return (
    <>
      <HomeProductOverview />

      {/* The Problem Section */}
      <ProblemSection>
        <ProblemContainer>
          <ProblemContent>
            <ProblemText>
              <ProblemTitle>
                Scattered Tools vs. Your Rental Command{"\u00A0"}Center
              </ProblemTitle>
              <ProblemDescription>
                Spreadsheets for rent. Another app for repairs. A third for
                taxes. Full-service PMs often take about 8% to 10% of rent, or
                you run a verified Asset Verification System in software. One OS:
                your AI First Responder, Automated Triage &amp; Dispatch,
                real-time DSCR, and instant Schedule E export for your Closing
                Vault.
              </ProblemDescription>
            </ProblemText>
            <ProblemVisual>
              <ChaosView>
                <ChaosPhone>
                  <PhoneMockup>
                    <NotificationBadge>23</NotificationBadge>
                    <MessageRow>
                      <MessageOutlined /> 11:47 PM: Tenant: "URGENT - kitchen
                      faucet dripping"
                    </MessageRow>
                    <MessageRow>
                      <MessageOutlined /> Saturday 8am: "Can I paint the walls?"
                    </MessageRow>
                    <MessageRow>
                      <PhoneOutlined /> Sunday: Missed call from tenant (4)
                    </MessageRow>
                    <MessageRow>
                      <MessageOutlined /> 2am: "Is it okay if my friend visits?"
                    </MessageRow>
                    <MessageRow>
                      <MessageOutlined /> Friday night: "The door squeaks - can
                      you fix it now?"
                    </MessageRow>
                    <MessageRow>
                      <MessageOutlined /> 10pm: "I lost my key" (they found it 5
                      min later)
                    </MessageRow>
                    <MessageRow>
                      <ExclamationCircleOutlined /> Context scattered across
                      tools
                    </MessageRow>
                  </PhoneMockup>
                </ChaosPhone>
              </ChaosView>
              <VSText>vs</VSText>
              <LeoView>
                <LeoInterface>
                  <ProblemChatHeader>
                    <img
                      src="/icons/leo/leo.png"
                      alt="Leo AI"
                      style={{
                        width: "20px",
                        height: "20px",
                        marginRight: "8px",
                        filter:
                          "brightness(0) saturate(100%) invert(15%) sepia(95%) saturate(1352%) hue-rotate(85deg) brightness(0.85) contrast(1.1)",
                      }}
                    />{" "}
                    Leo AI · Your First Responder
                  </ProblemChatHeader>
                  <LeoMessage>
                    <LeoCheckItem>
                      <CheckCircleOutlined /> AC repair tomorrow 2-4pm
                    </LeoCheckItem>
                    <LeoCheckItem>
                      <CheckCircleOutlined /> Plumber confirmed Friday
                    </LeoCheckItem>
                    <LeoCheckItem>
                      <CheckCircleOutlined /> Rent reminders sent
                    </LeoCheckItem>
                    <LeoCheckItem>
                      <CheckCircleOutlined /> All notifications handled
                    </LeoCheckItem>
                  </LeoMessage>
                  <LeoStatus>Handled. You&apos;re covered.</LeoStatus>
                </LeoInterface>
              </LeoView>
            </ProblemVisual>
          </ProblemContent>
        </ProblemContainer>
      </ProblemSection>

      {/* Meet Leo Section */}
      <MeetLeoSection>
        <MeetLeoContainer>
          <MeetLeoTextContent>
            <SectionBadge>Your AI First Responder</SectionBadge>
            <MeetLeoTitle>
              Tenant messages never hit your phone first.
            </MeetLeoTitle>
            <MeetLeoLead>
              Leo triages every tenant thread before it becomes a midnight ping.
            </MeetLeoLead>
            <MeetBulletList>
              <MeetBullet>
                <MeetBulletIconWrap
                  role="img"
                  aria-label="Verified tenant transcript"
                >
                  <SafetyOutlined aria-hidden />
                </MeetBulletIconWrap>
                <MeetBulletText>
                  Routine rent and repair questions stay in a verified
                  transcript - not your personal texts.
                </MeetBulletText>
              </MeetBullet>
              <MeetBullet>
                <MeetBulletIconWrap
                  role="img"
                  aria-label="Route: Automated Triage and Dispatch"
                >
                  <NodeIndexOutlined aria-hidden />
                </MeetBulletIconWrap>
                <MeetBulletText>
                  When the lease says a vendor belongs in the loop, Automated
                  Triage &amp; Dispatch handles it. Not your SMS thread.
                </MeetBulletText>
              </MeetBullet>
              <MeetBullet>
                <MeetBulletIconWrap
                  role="img"
                  aria-label="Record: investor-ready context and Schedule E"
                >
                  <FileTextOutlined aria-hidden />
                </MeetBulletIconWrap>
                <MeetBulletText>
                  Your dashboard keeps investor-ready context for diligence and{" "}
                  {"Schedule\u00a0E"}, not screenshots in three apps.
                </MeetBulletText>
              </MeetBullet>
            </MeetBulletList>
            <MeetLeoFooter>
              You set the rules and approve exceptions. Leo runs the front desk.
            </MeetLeoFooter>
          </MeetLeoTextContent>
          <MeetLeoVisual>
            <MeetLeoChatEmbed>
              <StaticOpsChatPreview
                layout="card"
                aria-label={`${APP_NAME} AI First Responder: sample summary thread, static preview only.`}
                headerSubtitle="Short landlord updates · not every midnight ping"
                previewBody="Sample conversation only, not interactive."
                messages={AI_FIRST_RESPONDER_DEMO_MESSAGES}
              />
            </MeetLeoChatEmbed>
          </MeetLeoVisual>
        </MeetLeoContainer>
      </MeetLeoSection>
    </>
  );
}

const ProblemSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(220, 53, 69, 0.05) 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const ProblemContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const ProblemTitle = styled.h2`
  ${marketingSectionHeading}
  color: #495057;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-wrap: balance;
`;

const ProblemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const ProblemText = styled.div`
  text-align: center;
  margin-bottom: 1rem;
`;

const ProblemDescription = styled.p`
  font-size: 1.375rem;
  line-height: 1.7;
  color: #6c757d;
  font-family: var(--font-secondary);
  margin-top: 0.5rem;
  margin-bottom: 3rem;
  font-weight: 400;

  @media ${device.mobile} {
    font-size: 1.15rem;
    margin-bottom: 2rem;
  }
`;

const ProblemVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  max-width: 1000px;
  margin: 0 auto;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const ChaosView = styled.div`
  flex: 1;
  max-width: 420px;
`;

const ChaosPhone = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 1.75rem;
  box-shadow: 0 12px 48px rgba(220, 53, 69, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
  border: 3px solid #dc3545;
  width: 100%;
  max-width: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 56px rgba(220, 53, 69, 0.2),
      0 8px 24px rgba(0, 0, 0, 0.1);
  }

  @media ${device.mobile} {
    padding: 1.5rem;
    border-radius: 20px;
  }
`;

const PhoneMockup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -12px;
  right: -12px;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.5),
    0 0 0 3px rgba(255, 255, 255, 0.9);
  animation: notificationPulse 2s ease-in-out infinite;

  @keyframes notificationPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%);
  border-radius: 10px;
  font-size: 0.9rem;
  color: #495057;
  border-left: 3px solid #dee2e6;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
    border-left-color: #adb5bd;
    transform: translateX(2px);
  }

  .anticon {
    color: #6c757d;
    font-size: 1.1rem;
  }
`;

const VSText = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #495057;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 2px;
    height: 40px;
    background: linear-gradient(
      180deg,
      transparent 0%,
      #adb5bd 50%,
      transparent 100%
    );
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }

  @media ${device.mobile} {
    padding: 1rem 0;
    font-size: 1.75rem;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const LeoView = styled.div`
  flex: 1;
  max-width: 450px;
`;

const LeoInterface = styled.div`
  background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
  border-radius: 24px;
  padding: 1.75rem;
  box-shadow: 0 12px 48px rgba(44, 95, 45, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 3px solid var(--color-primary);
  width: 100%;
  max-width: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      var(--color-primary-light) 100%
    );
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 56px rgba(44, 95, 45, 0.25),
      0 8px 24px rgba(0, 0, 0, 0.1);
  }

  @media ${device.mobile} {
    padding: 1.5rem;
    border-radius: 20px;
  }
`;

const ProblemChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 1.25rem;
  font-size: 1.15rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(74, 124, 89, 0.15);

  .anticon {
    font-size: 1.5rem;
    filter: drop-shadow(0 2px 4px rgba(74, 124, 89, 0.2));
  }
`;

const LeoMessage = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  color: var(--color-text);
  margin-bottom: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 2px solid rgba(74, 124, 89, 0.2);
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

const LeoCheckItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  line-height: 1.4;
  color: var(--color-text);
  white-space: nowrap;

  .anticon {
    color: var(--color-primary);
    font-size: 1.2rem;
    flex-shrink: 0;
  }
`;

const LeoStatus = styled.div`
  text-align: center;
  color: var(--color-primary);
  font-weight: 700;
  font-size: 1.05rem;
  padding: 0.75rem;
  background: rgba(74, 124, 89, 0.08);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

// Meet Leo Section Styles
const MeetLeoSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
  position: relative;

  @media ${device.mobile} {
    padding: 5rem 1.5rem;
  }
`;

const MeetLeoContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 3rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 3rem;
  }
`;

const MeetLeoTextContent = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;

  @media ${device.minTablet} {
    flex: 1.45;
  }

  @media ${device.mobile} {
    text-align: center;
  }
`;

const MeetLeoVisual = styled.div`
  flex: 1;
  min-width: 0;

  @media ${device.minTablet} {
    flex: 0.82;
  }
`;

const MeetLeoChatEmbed = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`;

const SectionBadge = styled.div`
  display: inline-block;
  background: var(--color-primary);
  color: white;
  padding: 0.35rem 1rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 1.1rem;
`;

const MeetLeoTitle = styled.h2`
  font-size: clamp(1.65rem, 3.2vw, 2.35rem);
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.65rem;
  font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  letter-spacing: -0.025em;
  line-height: 1.18;
  max-width: min(38rem, 100%);

  @media ${device.mobile} {
    max-width: 100%;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
`;

const MeetLeoLead = styled.p`
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0 0 1.35rem;
  max-width: min(40rem, 100%);

  @media ${device.mobile} {
    font-size: 0.98rem;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    max-width: 100%;
  }
`;

const MeetBulletList = styled.ul`
  list-style: none;
  margin: 0 0 1.25rem;
  padding: 0;
  max-width: min(44rem, 100%);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media ${device.mobile} {
    max-width: 100%;
    text-align: left;
  }
`;

const MeetBullet = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.35rem 0.85rem;
  align-items: start;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(44, 95, 45, 0.12);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`;

const MeetBulletIconWrap = styled.span`
  grid-column: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 1.65rem;
  line-height: 1;
  color: var(--color-primary-dark);
  padding-top: 0.12rem;

  .anticon {
    display: block;
  }

  @media ${device.mobile} {
    grid-column: 1;
    padding-top: 0;
  }
`;

const MeetBulletText = styled.span`
  grid-column: 2;
  font-size: 0.9rem;
  line-height: 1.58;
  color: var(--color-text);
  font-family: var(--font-secondary);
  font-weight: 400;

  @media ${device.mobile} {
    grid-column: 1;
  }
`;

const MeetLeoFooter = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--color-primary-dark);
  font-family: var(--font-secondary);
  font-weight: 600;
  margin: 0;
  max-width: min(40rem, 100%);

  @media ${device.mobile} {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
  }
`;
